import { useEffect, useState } from "react"
import { H4 } from "components/ui/typography/titles/Titles"
import { Select } from "components/ui/select/Select"
import { normalizeCategoryValue } from "helpers/categories.helper"
import { useCategorySelection } from "hooks/categorySelection.hook"
import { DetachModal } from "./layout/DetachModal/DetachModal"
import type { TCategoryHierarchy } from "types/modelNumbers.type"
import style from "./Categories.module.scss"

const { categories, selectBlock, selectLabel, detachButton, topBlock } = style

type Props = {
	modelNumberId?: number
	categoryHierarchy?: TCategoryHierarchy
	modelNumberName?: string
	onDirtyChange?: (isDirty: boolean) => void
	onRelatedCategoryChange?: (relatedCategoryId: number | null) => void
}

const SELECT_CONFIGS: { label: string; key: keyof TCategoryHierarchy }[] = [
	{ label: "Top level category", key: "Top Level Category" },
	{ label: "Super category", key: "Sub Category" },
	{ label: "Model category", key: "Model Category" },
]

export const Categories = ({
	modelNumberId,
	categoryHierarchy,
	modelNumberName,
	onDirtyChange,
	onRelatedCategoryChange,
}: Props) => {
	const [isDetachModalOpen, setDetachModalOpen] = useState(false)
	const [isDetachDirtySuppressed, setIsDetachDirtySuppressed] = useState(false)
	const [isDefaultsSyncing, setIsDefaultsSyncing] = useState(true)

	const defaultTopLevelCategory = categoryHierarchy?.["Top Level Category"]?.name ?? null
	const defaultSuperCategory = categoryHierarchy?.["Sub Category"]?.name ?? null
	const defaultModelCategoryId = categoryHierarchy?.["Model Category"]?.id ?? null
	const defaultModelCategoryName = categoryHierarchy?.["Model Category"]?.name ?? null

	const {
		selectedTopLevelCategory,
		selectedSuperCategory,
		selectedModelCategory,
		selectedModelCategoryId,
		topLevelOptions,
		superCategoryOptions,
		modelCategoryOptions,
		handleTopLevelChange,
		handleSuperCategoryChange,
		handleModelCategoryChange,
		resetAll,
		syncDefaults,
		isAnyCategorySelected,
	} = useCategorySelection({
		defaultTopLevelCategory,
		defaultSuperCategory,
		defaultModelCategoryId,
		defaultModelCategoryName,
	})

	// Dirty tracking
	useEffect(() => {
		if (isDetachDirtySuppressed || isDefaultsSyncing) {
			onDirtyChange?.(false)
			return
		}

		const isTopLevelDirty =
			normalizeCategoryValue(selectedTopLevelCategory) !== normalizeCategoryValue(defaultTopLevelCategory)
		const isSuperDirty = normalizeCategoryValue(selectedSuperCategory) !== normalizeCategoryValue(defaultSuperCategory)
		const isModelDirty =
			normalizeCategoryValue(selectedModelCategory) !== normalizeCategoryValue(defaultModelCategoryId)

		onDirtyChange?.(isTopLevelDirty || isSuperDirty || isModelDirty)
	}, [
		isDetachDirtySuppressed,
		isDefaultsSyncing,
		defaultModelCategoryId,
		defaultSuperCategory,
		defaultTopLevelCategory,
		onDirtyChange,
		selectedModelCategory,
		selectedSuperCategory,
		selectedTopLevelCategory,
	])

	// Notify parent of related category changes
	useEffect(() => {
		onRelatedCategoryChange?.(selectedModelCategoryId)
	}, [onRelatedCategoryChange, selectedModelCategoryId])

	// Sync defaults when categoryHierarchy changes
	useEffect(() => {
		setIsDefaultsSyncing(true)
		syncDefaults()
		setDetachModalOpen(false)
		setIsDetachDirtySuppressed(false)
	}, [syncDefaults])

	// Detect when defaults are synced
	useEffect(() => {
		if (!isDefaultsSyncing) return

		const isTopLevelSynced =
			normalizeCategoryValue(selectedTopLevelCategory) === normalizeCategoryValue(defaultTopLevelCategory)
		const isSuperSynced = normalizeCategoryValue(selectedSuperCategory) === normalizeCategoryValue(defaultSuperCategory)
		const isModelSynced =
			normalizeCategoryValue(selectedModelCategory) === normalizeCategoryValue(defaultModelCategoryId)

		if (isTopLevelSynced && isSuperSynced && isModelSynced) {
			setIsDefaultsSyncing(false)
		}
	}, [
		defaultModelCategoryId,
		defaultSuperCategory,
		defaultTopLevelCategory,
		isDefaultsSyncing,
		selectedModelCategory,
		selectedSuperCategory,
		selectedTopLevelCategory,
	])

	const closeDetachModal = () => setDetachModalOpen(false)

	const onDetachSuccessHandle = () => {
		resetAll()
		setIsDetachDirtySuppressed(true)
	}

	return (
		<div className={categories}>
			<div className={topBlock}>
				<H4>Categories</H4>
				{isAnyCategorySelected && (
					<button className={detachButton} onClick={() => setDetachModalOpen(true)}>
						Detach
					</button>
				)}
			</div>
			<DetachModal
				isModalOpen={isDetachModalOpen}
				modelNumberId={modelNumberId}
				modelNumberName={modelNumberName}
				onCancel={closeDetachModal}
				onSuccess={onDetachSuccessHandle}
			/>
			{SELECT_CONFIGS.map(({ label, key }) => {
				const isTopLevel = key === "Top Level Category"
				const isSuper = key === "Sub Category"
				const options = isTopLevel ? topLevelOptions : isSuper ? superCategoryOptions : modelCategoryOptions
				const value = isTopLevel ? selectedTopLevelCategory : isSuper ? selectedSuperCategory : selectedModelCategory

				return (
					<div key={key} className={selectBlock}>
						<span className={selectLabel}>{label}</span>
						<Select
							variant="outlined"
							options={options}
							value={value}
							placeholder="Select category"
							onChange={(newValue) => {
								setIsDefaultsSyncing(false)
								setIsDetachDirtySuppressed(false)
								if (isTopLevel) {
									handleTopLevelChange(newValue)
									return
								}
								if (isSuper) {
									handleSuperCategoryChange(newValue)
									return
								}
								handleModelCategoryChange(newValue)
							}}
							showSearch
							optionFilterProp="label"
						/>
					</div>
				)
			})}
		</div>
	)
}
